import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  ExpandMore as ExpandMoreIcon,
  FactCheck as FactCheckIcon,
  GitHub as GitHubIcon,
  Save as SaveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Navigate } from 'react-router-dom';
import { deleteAdminUser, draftFeedback, fetchAdminUsers, fetchFeedbackMessages, fetchLearnerSignals, inviteAdminUser, sendFeedback, updateAdminUser } from '../api/adminApi';
import { addCohortMembership, createCohort, fetchCohorts } from '../api/cohortApi';
import { analyzeEvidence } from '../api/evidenceApi';
import { fetchDefaultGithubTokenStatus, patchDefaultGithubToken } from '../api/settingsApi';
import EvidenceIntelligenceDialog from '../components/evidence/EvidenceIntelligenceDialog';
import { useAuth } from '../auth/AuthContext';
import { LEARNING_PATHS as ALL_LEARNING_PATHS } from '../constants/learningPaths';

// Display order for the "Assigned learning paths" checkboxes. Labels come from
// the single learning-paths source of truth (src/constants/learningPaths.js) —
// add a new path there first, then list its slug here to surface it in Admin.
const ADMIN_PATH_ORDER = ['data-analysis', 'azure', 'azure-platform', 'python', 'sql', 'data-science', 'full-stack', 'web-dev'];
const LEARNING_PATHS = ADMIN_PATH_ORDER
  .map((slug) => ALL_LEARNING_PATHS.find((path) => path.id === slug))
  .filter(Boolean)
  .map((path) => ({ slug: path.id, label: path.jobReadyLabel }));

const ROLES = ['learner', 'admin', 'platform_owner'];
const STATUSES = ['invited', 'active', 'paused', 'completed'];
const DEFAULT_COHORT = 'qode-clarity-data-analyst-cohort-001';

const COHORT_TRACKS = [
  { slug: 'data-analyst-job-ready', label: 'Data Analyst Job Ready', learningPath: 'data-analysis' },
  { slug: 'azure-data-engineer', label: 'Azure Data Engineer', learningPath: 'azure' },
  { slug: 'azure-platform-engineer', label: 'Azure Platform Engineer', learningPath: 'azure-platform' },
  { slug: 'python-job-ready', label: 'Python Job Ready', learningPath: 'python' },
  { slug: 'sql-job-ready', label: 'SQL Job Ready', learningPath: 'sql' },
  { slug: 'data-scientist-job-ready', label: 'Data Scientist Job Ready', learningPath: 'data-science' },
  { slug: 'full-stack-developer-job-ready', label: 'Full-Stack Developer Job Ready', learningPath: 'full-stack' },
];

const canAdmin = (user) => ['admin', 'platform_owner'].includes(user?.role);

const isNeedsOnboarding = (user) => !(user.learning_paths || []).length && !(user.cohort_key || '').trim();

const AdminStat = ({ label, value, tone = 'default' }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      border: 1,
      borderColor: tone === 'warning' ? 'warning.light' : tone === 'success' ? 'success.light' : 'divider',
      bgcolor: tone === 'default' ? 'background.paper' : 'action.hover',
      minWidth: 140,
    }}
  >
    <Typography variant="caption" color="text.secondary" fontWeight={800}>{label}</Typography>
    <Typography variant="h5" fontWeight={900}>{value}</Typography>
  </Paper>
);

const signalColor = {
  waiting_setup: 'warning',
  at_risk: 'error',
  high_signal: 'success',
  active: 'default',
};

const signalLabel = {
  waiting_setup: 'Waiting setup',
  at_risk: 'At risk',
  high_signal: 'High signal',
  active: 'Active',
};

const CHART_TONES = {
  primary: '#1e88e5',
  primarySoft: '#90caf9',
  warning: '#ef6c00',
  risk: '#d32f2f',
  success: '#2e7d32',
  neutral: '#9e9e9e',
};

const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + (radius * Math.cos(angleInRadians)),
    y: centerY + (radius * Math.sin(angleInRadians)),
  };
};

const describeArc = (x, y, radius, startAngle, endAngle) => {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return [
    'M', start.x, start.y,
    'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y,
  ].join(' ');
};

const SignalDistributionChart = ({ summary }) => {
  const segments = [
    { key: 'waiting_setup', label: 'Waiting setup', value: summary.waiting_setup || 0, color: CHART_TONES.warning },
    { key: 'at_risk', label: 'At risk', value: summary.at_risk || 0, color: CHART_TONES.risk },
    { key: 'high_signal', label: 'High signal', value: summary.high_signal || 0, color: CHART_TONES.success },
    { key: 'active', label: 'Active', value: summary.active || 0, color: CHART_TONES.neutral },
  ];
  const total = Math.max(segments.reduce((sum, item) => sum + item.value, 0), 1);
  let currentAngle = 0;

  return (
    <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
      <Typography variant="subtitle2" fontWeight={900}>Learner signal mix</Typography>
      <Typography variant="caption" color="text.secondary">
        Where admin attention is currently concentrated.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <Box sx={{ position: 'relative', width: 150, height: 150, flex: '0 0 auto' }}>
          <svg viewBox="0 0 160 160" width="150" height="150" role="img" aria-label="Learner signal donut chart">
            <circle cx="80" cy="80" r="54" fill="none" stroke="#eceff1" strokeWidth="22" />
            {segments.map((item) => {
              const angle = (item.value / total) * 360;
              const start = currentAngle;
              const end = currentAngle + angle;
              currentAngle = end;
              if (!item.value) return null;
              return (
                <path
                  key={item.key}
                  d={describeArc(80, 80, 54, start, end)}
                  fill="none"
                  stroke={item.color}
                  strokeWidth="22"
                  strokeLinecap="round"
                />
              );
            })}
          </svg>
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
            <Box textAlign="center">
              <Typography variant="h5" fontWeight={900}>{total}</Typography>
              <Typography variant="caption" color="text.secondary">learners</Typography>
            </Box>
          </Box>
        </Box>
        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
          {segments.map((item) => (
            <Stack key={item.key} direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: item.color }} />
              <Typography variant="caption" sx={{ flex: 1 }}>{item.label}</Typography>
              <Typography variant="caption" fontWeight={900}>{item.value}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};

const VerticalBarChart = ({ title, subtitle, rows = [], empty = 'No data yet.' }) => {
  const visibleRows = rows.slice(0, 8);
  const maxValue = Math.max(...visibleRows.map((row) => Number(row.value) || 0), 1);

  return (
    <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
      <Typography variant="subtitle2" fontWeight={900}>{title}</Typography>
      {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ mt: 2, height: 170 }}>
        {visibleRows.map((row, index) => (
          <Stack key={`${row.label}-${index}`} spacing={0.75} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" fontWeight={900}>{row.value ?? 0}{row.suffix || ''}</Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 44,
                minHeight: row.value ? 10 : 2,
                height: `${((Number(row.value) || 0) / maxValue) * 120}px`,
                bgcolor: row.color || CHART_TONES.primary,
                borderRadius: '6px 6px 2px 2px',
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              title={row.label}
              sx={{
                width: '100%',
                minHeight: 32,
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {row.label}
            </Typography>
          </Stack>
        ))}
        {!visibleRows.length && <Typography variant="caption" color="text.secondary">{empty}</Typography>}
      </Stack>
    </Paper>
  );
};

const VisitorFunnelChart = ({ rows }) => {
  const funnelRows = (rows || []).map((row, index) => ({
    ...row,
    color: index === 0 ? CHART_TONES.primarySoft : index < 3 ? CHART_TONES.primary : CHART_TONES.warning,
  }));
  const maxValue = Math.max(...funnelRows.map((row) => row.value), 1);

  return (
    <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
      <Typography variant="subtitle2" fontWeight={900}>Visitor to setup funnel</Typography>
      <Typography variant="caption" color="text.secondary">
        Early signs of people arriving, registering, and needing admin action.
      </Typography>
      <Stack spacing={1} sx={{ mt: 2 }}>
        {funnelRows.map((row) => (
          <Box key={row.label} sx={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 170px) 1fr 48px', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" title={row.label}>{row.label}</Typography>
            <Box sx={{ height: 22, display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  height: 22,
                  width: `${(row.value / maxValue) * 100}%`,
                  minWidth: row.value ? 18 : 2,
                  bgcolor: row.color,
                  clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)',
                }}
              />
            </Box>
            <Typography variant="caption" fontWeight={900} textAlign="right">{row.value}</Typography>
          </Box>
        ))}
        {!funnelRows.length && <Typography variant="caption" color="text.secondary">No funnel data yet.</Typography>}
      </Stack>
    </Paper>
  );
};

const EngagementBarChart = ({ rows = [], averageScore = 0 }) => (
  <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
    <Typography variant="subtitle2" fontWeight={900}>Engagement bands</Typography>
    <Typography variant="caption" color="text.secondary">
      Average engagement score: {averageScore}/100
    </Typography>
    <Stack spacing={1.1} sx={{ mt: 2 }}>
      {rows.map((row) => (
        <Box key={row.label}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption">{row.label}</Typography>
            <Typography variant="caption" fontWeight={900}>{row.value}</Typography>
          </Stack>
          <Box sx={{ height: 10, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${(row.value / Math.max(...rows.map((item) => item.value), 1)) * 100}%`, bgcolor: row.color, minWidth: row.value ? 8 : 0 }} />
          </Box>
        </Box>
      ))}
      {!rows.length && <Typography variant="caption" color="text.secondary">No engagement data yet.</Typography>}
    </Stack>
  </Paper>
);

const TimeToActionChart = ({ rows = [] }) => (
  <VerticalBarChart
    title="Time to action"
    subtitle="Average hours between key learner steps."
    rows={rows
      .filter((row) => row.value !== null && row.value !== undefined)
      .map((row) => ({
        label: `${row.label} (${row.sample_count})`,
        value: row.value,
        suffix: 'h',
        color: CHART_TONES.primary,
      }))}
    empty="Not enough learner movement yet."
  />
);

const FollowUpQueue = ({ rows = [] }) => (
  <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
    <Typography variant="subtitle2" fontWeight={900}>Follow-up queue</Typography>
    <Typography variant="caption" color="text.secondary">
      Learners who need a human nudge before they drift.
    </Typography>
    <Stack spacing={1} sx={{ mt: 1.5 }}>
      {rows.slice(0, 8).map((item) => (
        <Box key={`${item.user_id}-${item.reason}`} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={900}>{item.name || item.email}</Typography>
              <Typography variant="caption" color="text.secondary">{item.reason}</Typography>
            </Box>
            <Chip size="small" label={item.priority} color={item.priority === 'high' ? 'error' : 'warning'} />
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
            {item.recommended_action}
          </Typography>
        </Box>
      ))}
      {!rows.length && <Typography variant="caption" color="text.secondary">No follow-up queue yet.</Typography>}
    </Stack>
  </Paper>
);

const AdminActions = ({ rows = [] }) => (
  <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
    <Typography variant="subtitle2" fontWeight={900}>Admin action log</Typography>
    <Typography variant="caption" color="text.secondary">
      Recent setup changes made by admins.
    </Typography>
    <Stack spacing={0.75} sx={{ mt: 1.5 }}>
      {rows.slice(0, 8).map((item, index) => (
        <Stack key={`${item.created_at}-${index}`} direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={item.event} variant="outlined" />
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {(item.metadata?.email || item.target_user_id || 'Unknown user')} · {item.created_at ? new Date(item.created_at).toLocaleString('en-GB') : 'unknown time'}
          </Typography>
        </Stack>
      ))}
      {!rows.length && <Typography variant="caption" color="text.secondary">No admin actions captured yet.</Typography>}
    </Stack>
  </Paper>
);

const LearnerSignalsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('attention');

  useEffect(() => {
    setLoading(true);
    fetchLearnerSignals()
      .then(setData)
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Alert severity="info">Loading learner signals...</Alert>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const learners = (data.learners || []).filter((item) => {
    if (view === 'attention') return ['waiting_setup', 'at_risk'].includes(item.status);
    if (view === 'high_signal') return item.status === 'high_signal';
    return true;
  });
  const engagementRows = Object.entries(data.engagement?.counts || {}).map(([key, value]) => ({
    label: key.replace('_', ' '),
    value,
    color: key === 'at_risk' ? CHART_TONES.risk : key === 'strong' ? CHART_TONES.success : key === 'active' ? CHART_TONES.primary : CHART_TONES.warning,
  }));

  return (
    <Stack spacing={2}>
      <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', md: 'repeat(6, 1fr)' }} gap={1}>
        <AdminStat label="All users" value={data.summary.total_users ?? data.summary.total_learners} />
        <AdminStat label="Learner accounts" value={data.summary.total_learners} />
        <AdminStat label="Waiting setup" value={data.summary.waiting_setup} tone="warning" />
        <AdminStat label="At risk" value={data.summary.at_risk} tone="warning" />
        <AdminStat label="High signal" value={data.summary.high_signal} tone="success" />
        <AdminStat label="Visitors 7d" value={data.summary.anonymous_visitors_7d} />
      </Box>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={1.5}>
        <SignalDistributionChart summary={data.summary} />
        <VisitorFunnelChart rows={data.dropoff_funnel || []} />
      </Box>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={1.5}>
        <EngagementBarChart
          rows={engagementRows}
          averageScore={data.engagement?.average_score ?? 0}
        />
        <TimeToActionChart rows={data.time_to_action || []} />
      </Box>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={1.5}>
        <VerticalBarChart
          title="Path demand"
          subtitle="What learners are asking for or already assigned to."
          rows={(data.path_demand || []).map((row) => ({ ...row, color: CHART_TONES.primary }))}
        />
        <VerticalBarChart
          title="Thought pattern tags"
          subtitle="Signals inferred from goals, notes, confidence, and evidence habits."
          rows={(data.thought_patterns || []).map((row) => ({ ...row, color: CHART_TONES.warning }))}
        />
      </Box>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={1.5}>
        <FollowUpQueue rows={data.follow_up_queue || []} />
        <AdminActions rows={data.admin_actions || []} />
      </Box>

      <Paper elevation={0} sx={{ p: 1.5, border: 1, borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <Typography variant="subtitle2" fontWeight={900} sx={{ flex: 1 }}>
            Learner intelligence
          </Typography>
          <TextField
            select
            size="small"
            label="Signal view"
            value={view}
            onChange={(event) => setView(event.target.value)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="attention">Needs attention</MenuItem>
            <MenuItem value="high_signal">High-signal learners</MenuItem>
            <MenuItem value="all">All learners</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {(data.anonymous_visitors || []).length > 0 && (
        <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle2" fontWeight={900}>Anonymous visitors</Typography>
              <Typography variant="caption" color="text.secondary">
                Recent visitors who browsed before registering or signing in.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={1}>
              {data.anonymous_visitors.map((visitor) => (
                <Box key={visitor.session_id} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <Typography variant="body2" fontWeight={800} sx={{ flex: 1 }}>
                      Session {visitor.session_id.slice(0, 8)}
                    </Typography>
                    <Chip size="small" label={`${visitor.event_count} event${visitor.event_count === 1 ? '' : 's'}`} variant="outlined" />
                    <Typography variant="caption" color="text.secondary">
                      Last: {visitor.last_seen_at ? new Date(visitor.last_seen_at).toLocaleString('en-GB') : 'unknown'}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                    Pages: {(visitor.pages || []).slice(0, 5).join(', ') || 'No page captured'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <Stack spacing={1}>
        {learners.map((item) => (
          <Accordion
            key={item.user.id}
            disableGutters
            elevation={0}
            sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ width: '100%', pr: 1 }}>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography variant="subtitle2" fontWeight={900}>
                    {item.user.first_name} {item.user.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{item.user.email}</Typography>
                </Box>
                <Chip size="small" label={signalLabel[item.status] || item.status} color={signalColor[item.status] || 'default'} />
                <Chip size="small" label={`${item.lessons_completed}/${item.lessons_started} lessons`} variant="outlined" />
                <Chip size="small" label={`${item.engagement_score || 0}/100 engagement`} variant="outlined" />
                <Chip size="small" label={`${item.events_7d} events 7d`} variant="outlined" />
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 150 }}>
                  Last: {item.last_activity_at ? new Date(item.last_activity_at).toLocaleDateString('en-GB') : 'none'}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Alert severity={item.status === 'at_risk' ? 'warning' : 'info'} sx={{ mb: 1.5 }}>
                    {item.recommended_action}
                  </Alert>
                  <Stack spacing={0.75}>
                    {item.signals.map((signal) => (
                      <Typography key={signal} variant="body2">- {signal}</Typography>
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack spacing={0.75}>
                    <Typography variant="caption" color="text.secondary">Average confidence: {item.avg_confidence || 0}%</Typography>
                    <Typography variant="caption" color="text.secondary">Journal count: {item.journal_count}</Typography>
                    <Typography variant="caption" color="text.secondary">Engagement band: {item.engagement_band?.replace('_', ' ') || 'unknown'}</Typography>
                    <Typography variant="caption" color="text.secondary">Cohort: {item.user.cohort_key || 'Not assigned'}</Typography>
                    <Typography variant="caption" color="text.secondary">Paths: {(item.user.learning_paths || []).join(', ') || 'None'}</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, pt: 0.5 }}>
                      {(item.thought_tags || []).map((tag) => (
                        <Chip key={tag} size="small" label={tag} variant="outlined" />
                      ))}
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight={900}>Recent events</Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                    {(item.recent_events || []).map((event) => (
                      <Typography key={`${event.event_type}-${event.created_at}`} variant="caption" color="text.secondary">
                        {event.event_type} · {event.page || 'no page'}
                      </Typography>
                    ))}
                    {!item.recent_events?.length && <Typography variant="caption" color="text.secondary">No tracked events yet.</Typography>}
                  </Stack>
                  <Typography variant="caption" fontWeight={900} display="block" sx={{ mt: 1.5 }}>Timeline</Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                    {(item.timeline || []).map((event) => (
                      <Typography key={`${event.label}-${event.at}`} variant="caption" color="text.secondary">
                        {new Date(event.at).toLocaleDateString('en-GB')} · {event.label}
                      </Typography>
                    ))}
                    {!item.timeline?.length && <Typography variant="caption" color="text.secondary">No timeline yet.</Typography>}
                  </Stack>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
        {!learners.length && <Alert severity="info">No learners in this signal view.</Alert>}
      </Stack>
    </Stack>
  );
};

const CohortsPanel = ({ cohorts: externalCohorts = [], onCohortsLoaded }) => {
  const [cohorts, setCohorts] = useState(externalCohorts);
  const [draft, setDraft] = useState({
    display_name: '',
    cohort_key: '',
    track_slug: 'data-analyst-job-ready',
    week_number: 1,
    curriculum_week: 1,
    shared_repo_url: '',
    shared_repo_token: '',
    notes: '',
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    fetchCohorts()
      .then((items) => {
        setCohorts(items);
        onCohortsLoaded?.(items);
      })
      .catch((error) => setStatus(error.message))
      .finally(() => setLoading(false));
  }, [onCohortsLoaded]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setCohorts(externalCohorts); }, [externalCohorts]);

  const updateDraft = (key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const save = async () => {
    setStatus('Creating cohort...');
    try {
      const result = await createCohort({
        ...draft,
        week_number: Number(draft.week_number || 1),
        curriculum_week: Number(draft.curriculum_week || draft.week_number || 1),
      });
      setStatus(`Created ${result.cohort_key}`);
      setDraft({
        display_name: '',
        cohort_key: '',
        track_slug: draft.track_slug,
        week_number: 1,
        curriculum_week: 1,
        shared_repo_url: '',
        shared_repo_token: '',
        notes: '',
      });
      load();
    } catch (error) {
      setStatus(error.message);
    }
  };

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={900}>Create cohort</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Creates a locked Week 1 shell and stores the cohort study plan. Learner journal saves use this selected cohort's study plan to find the learner's personal repo/PAT.
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={4}>
            <TextField label="Cohort name" value={draft.display_name} onChange={(event) => updateDraft('display_name', event.target.value)} fullWidth placeholder="Qode Clarity Data Analyst Cohort 002" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Cohort key" value={draft.cohort_key} onChange={(event) => updateDraft('cohort_key', event.target.value)} fullWidth placeholder="qode-clarity-data-analyst-cohort-002" helperText="Lowercase URL key; spaces become dashes on save." />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select label="Study plan" value={draft.track_slug} onChange={(event) => updateDraft('track_slug', event.target.value)} fullWidth>
              {COHORT_TRACKS.map((track) => <MenuItem key={track.slug} value={track.slug}>{track.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="Initial week" type="number" value={draft.week_number} onChange={(event) => updateDraft('week_number', event.target.value)} fullWidth inputProps={{ min: 1 }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="Curriculum week" type="number" value={draft.curriculum_week} onChange={(event) => updateDraft('curriculum_week', event.target.value)} fullWidth inputProps={{ min: 1 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Shared hackathon repo URL" value={draft.shared_repo_url} onChange={(event) => updateDraft('shared_repo_url', event.target.value)} fullWidth placeholder="https://github.com/org/cohort-hackathon" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Shared repo PAT" type="password" value={draft.shared_repo_token} onChange={(event) => updateDraft('shared_repo_token', event.target.value)} fullWidth placeholder="Optional" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Notes" value={draft.notes} onChange={(event) => updateDraft('notes', event.target.value)} fullWidth multiline minRows={2} />
          </Grid>
        </Grid>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mt: 2 }}>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={save}>Create cohort</Button>
          {status && <Typography variant="body2" color={status.startsWith('Created') ? 'success.main' : 'text.secondary'}>{status}</Typography>}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={900}>Existing cohorts</Typography>
        {loading ? (
          <Alert severity="info" sx={{ mt: 1 }}>Loading cohorts...</Alert>
        ) : (
          <Stack spacing={1.25} sx={{ mt: 1.5 }}>
            {cohorts.map((cohort) => (
              <Box key={cohort.cohort_key} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                  <Box>
                    <Typography fontWeight={900}>{cohort.name || cohort.cohort_key}</Typography>
                    <Typography variant="caption" color="text.secondary">{cohort.cohort_key}</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.75}>
                    <Chip size="small" label={COHORT_TRACKS.find((track) => track.slug === cohort.track_slug)?.label || cohort.track_slug || 'No study plan'} />
                    <Chip size="small" label={cohort.status || 'active'} variant="outlined" />
                    {cohort.shared_repo_url && <Chip size="small" label="Shared repo set" color="success" variant="outlined" />}
                    {cohort.has_shared_repo_token && <Chip size="small" label="Shared PAT set" color="success" variant="outlined" />}
                  </Stack>
                </Stack>
                {cohort.notes && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{cohort.notes}</Typography>}
              </Box>
            ))}
            {!cohorts.length && <Alert severity="info">No cohorts created yet.</Alert>}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

const InviteLearnerPanel = () => {
  const [draft, setDraft] = useState({
    first_name: '',
    email: '',
    send_email: true,
  });
  const [statusText, setStatusText] = useState('');

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setStatusText('');
  };

  const invite = async () => {
    if (!draft.email.trim()) {
      setStatusText('Add email');
      return;
    }
    setStatusText('Sending invite...');
    try {
      await inviteAdminUser({
        first_name: draft.first_name.trim(),
        email: draft.email.trim(),
        send_email: draft.send_email,
      });
      setStatusText(draft.send_email ? 'Invite sent' : 'Invite logged');
      setDraft((current) => ({
        ...current,
        first_name: '',
        email: '',
      }));
    } catch (error) {
      setStatusText(error.message || 'Could not send invite');
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
      <Typography variant="h6" fontWeight={900}>Invite learner</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Sends the learner the account registration link. Assign cohort and path after they register.
      </Typography>
      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={4}>
          <TextField label="First name" value={draft.first_name} onChange={(event) => updateDraft('first_name', event.target.value)} fullWidth />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField label="Email" type="email" value={draft.email} onChange={(event) => updateDraft('email', event.target.value)} fullWidth required />
        </Grid>
        <Grid item xs={12}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={draft.send_email} onChange={(event) => updateDraft('send_email', event.target.checked)} />}
              label="Send invite email"
            />
            <Button variant="contained" onClick={invite}>Send register link</Button>
            {statusText && <Typography variant="body2" color={statusText.includes('sent') || statusText.includes('created') ? 'success.main' : 'text.secondary'}>{statusText}</Typography>}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

const AdminUserCard = ({ user, currentUser, cohorts = [], onSaved, onDeleted }) => {
  const needsOnboarding = !(user.learning_paths || []).length && !(user.cohort_key || '').trim();
  const onboardingIntent = user.onboarding_intent || {};
  const submittedIntent = Boolean(onboardingIntent.submitted_at);
  const [draft, setDraft] = useState({
    learning_paths: user.learning_paths || [],
    cohort_key: user.cohort_key || '',
    account_status: user.account_status || 'active',
    role: user.role || 'learner',
    email_verified: user.email_verified || false,
  });
  const [status, setStatus] = useState('');
  const [membershipDraft, setMembershipDraft] = useState({
    cohort_key: cohorts[0]?.cohort_key || '',
  });
  const canEditRole = currentUser?.role === 'platform_owner';

  const togglePath = (path) => {
    setDraft((current) => ({
      ...current,
      learning_paths: current.learning_paths.includes(path)
        ? current.learning_paths.filter((item) => item !== path)
        : [...current.learning_paths, path]
    }));
    setStatus('Unsaved changes');
  };

  const save = async () => {
    setStatus('Saving...');
    try {
      const updated = await updateAdminUser(user.id, {
        learning_paths: draft.learning_paths,
        cohort_key: draft.cohort_key,
        account_status: draft.account_status,
        role: canEditRole ? draft.role : undefined,
        email_verified: draft.email_verified,
      });
      setStatus('Saved');
      onSaved(updated);
    } catch (error) {
      setStatus(error.message);
    }
  };

  const setAccountStatus = async (accountStatus) => {
    setStatus(accountStatus === 'paused' ? 'Suspending...' : 'Reactivating...');
    try {
      const updated = await updateAdminUser(user.id, {
        account_status: accountStatus,
      });
      setDraft((current) => ({ ...current, account_status: updated.account_status || accountStatus }));
      setStatus(accountStatus === 'paused' ? 'Suspended' : 'Reactivated');
      onSaved(updated);
    } catch (error) {
      setStatus(error.message);
    }
  };

  const removeUser = async () => {
    const label = `${user.first_name} ${user.last_name}`.trim() || user.email;
    if (!window.confirm(`Delete ${label}? This removes their account access.`)) return;
    setStatus('Deleting...');
    try {
      await deleteAdminUser(user.id);
      onDeleted(user.id);
    } catch (error) {
      setStatus(error.message);
    }
  };

  const addMembership = async () => {
    const selectedCohortKey = membershipDraft.cohort_key || cohorts[0]?.cohort_key || '';
    if (!selectedCohortKey) {
      setStatus('Choose a cohort to add');
      return;
    }
    const cohort = cohorts.find((item) => item.cohort_key === selectedCohortKey);
    setStatus('Adding cohort membership...');
    try {
      await addCohortMembership(selectedCohortKey, {
        user_id: user.id,
        track_slug: cohort?.track_slug || '',
        status: 'active',
      });
      setStatus('Cohort membership added');
    } catch (error) {
      setStatus(error.message);
    }
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 0.5 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          sx={{ width: '100%', pr: 1 }}
        >
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              {user.first_name} {user.last_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">{user.email}</Typography>
            {user.phone && (
              <Typography variant="caption" color="text.secondary" display="block">{user.phone}</Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
            <Chip size="small" label={user.role || 'learner'} color={user.role === 'platform_owner' ? 'secondary' : 'default'} />
            {(user.account_status || 'active') !== 'active' && <Chip size="small" label={user.account_status} color={user.account_status === 'paused' ? 'error' : 'warning'} />}
            <Chip size="small" label={user.email_verified ? 'Verified' : 'Unverified'} color={user.email_verified ? 'success' : 'warning'} variant="outlined" />
            {needsOnboarding && <Chip size="small" label={submittedIntent ? 'Needs onboarding' : 'No path yet'} color={submittedIntent ? 'error' : 'warning'} />}
            {(user.learning_paths || []).length > 0 && <Chip size="small" label={`${user.learning_paths.length} path${user.learning_paths.length > 1 ? 's' : ''}`} variant="outlined" />}
            {user.cohort_key && <Chip size="small" label={user.cohort_key} variant="outlined" />}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Stack spacing={1.25}>
              {submittedIntent ? (
                <Box sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5 }}>
                    Learner request
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Interests: {(onboardingIntent.interests || []).join(', ') || 'Not selected'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Follow up: {onboardingIntent.preferred_contact || 'email'}
                  </Typography>
                  {onboardingIntent.goal && (
                    <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-line' }}>
                      {onboardingIntent.goal}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Alert severity={needsOnboarding ? 'warning' : 'info'} sx={{ py: 0.5 }}>
                  {needsOnboarding ? 'No path or cohort assigned yet.' : 'Assignment details are below.'}
                </Alert>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Assigned learning paths</Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                {LEARNING_PATHS.map((path) => (
                  <FormControlLabel
                    key={path.slug}
                    control={<Checkbox size="small" checked={draft.learning_paths.includes(path.slug)} onChange={() => togglePath(path.slug)} />}
                    label={path.label}
                    sx={{ mr: 1 }}
                  />
                ))}
              </Stack>
            </Box>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={5}>
                <TextField
                  label="Cohort key"
                  value={draft.cohort_key}
                  onChange={(event) => {
                    setDraft((current) => ({ ...current, cohort_key: event.target.value }));
                    setStatus('Unsaved changes');
                  }}
                  fullWidth
                  placeholder={DEFAULT_COHORT}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Status"
                  value={draft.account_status}
                  onChange={(event) => {
                    setDraft((current) => ({ ...current, account_status: event.target.value }));
                    setStatus('Unsaved changes');
                  }}
                  fullWidth
                >
                  {STATUSES.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Role"
                  value={draft.role}
                  disabled={!canEditRole}
                  onChange={(event) => {
                    setDraft((current) => ({ ...current, role: event.target.value }));
                    setStatus('Unsaved changes');
                  }}
                  fullWidth
                >
                  {ROLES.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={draft.email_verified}
                  onChange={(e) => {
                    setDraft((current) => ({ ...current, email_verified: e.target.checked }));
                    setStatus('Unsaved changes');
                  }}
                />
              }
              label="Email verified"
            />
            {cohorts.length > 0 && (
              <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Additional cohort membership
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <TextField
                    select
                    label="Add learner to cohort"
                    value={membershipDraft.cohort_key || cohorts[0]?.cohort_key || ''}
                    onChange={(event) => setMembershipDraft({ cohort_key: event.target.value })}
                    size="small"
                    sx={{ minWidth: { xs: '100%', sm: 280 } }}
                  >
                    {cohorts.map((cohort) => (
                      <MenuItem key={cohort.cohort_key} value={cohort.cohort_key}>
                        {cohort.name || cohort.cohort_key}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button variant="outlined" onClick={addMembership}>
                    Add membership
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                  Multi-cohort learners will see this in Cohort Workspace and journal saves will follow that cohort's study plan.
                </Typography>
              </Box>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={save} sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}>
                Save assignment
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setDraft((current) => ({
                    ...current,
                    learning_paths: ['data-analysis'],
                    cohort_key: DEFAULT_COHORT,
                    account_status: 'active'
                  }));
                  setStatus('Unsaved changes');
                }}
              >
                Set Data Analyst Cohort 001
              </Button>
              {draft.account_status === 'paused' ? (
                <Button variant="outlined" color="success" onClick={() => setAccountStatus('active')}>
                  Reactivate
                </Button>
              ) : (
                <Button variant="outlined" color="warning" onClick={() => setAccountStatus('paused')}>
                  Suspend
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                onClick={removeUser}
                disabled={user.id === currentUser?.id || user.role === 'platform_owner'}
              >
                Delete
              </Button>
              {status && <Typography variant="body2" color={status === 'Saved' ? 'success.main' : 'text.secondary'}>{status}</Typography>}
            </Stack>
          </Stack>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

// Note: analyzeEvidence authenticates via the anonymous learner session cookie
// (qode_session_id), separate from the admin auth cookie used by adminApi.js —
// a 401 here just mints a fresh anonymous session, same as any other page.
const CurriculumAuditPanel = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditState, setAuditState] = useState('idle');
  const [auditResult, setAuditResult] = useState(null);
  const [auditError, setAuditError] = useState('');

  const runAudit = async () => {
    if (!content.trim()) return;
    setAuditOpen(true);
    setAuditState('loading');
    setAuditError('');
    try {
      const result = await analyzeEvidence({
        content,
        analysisType: 'curriculum-audit',
        context: { title },
      });
      setAuditResult(result);
      setAuditState('done');
    } catch (error) {
      setAuditError(error.message || 'Curriculum audit failed.');
      setAuditState('error');
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider' }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>Curriculum audit</Typography>
      <Stack spacing={1.5}>
        <TextField
          label="Lesson / curriculum title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          fullWidth
        />
        <TextField
          label="Paste curriculum or lesson markdown"
          value={content}
          onChange={(event) => setContent(event.target.value)}
          multiline
          minRows={8}
          fullWidth
        />
        <Button
          variant="contained"
          startIcon={<FactCheckIcon />}
          onClick={runAudit}
          disabled={!content.trim()}
          sx={{ alignSelf: 'flex-start' }}
        >
          Audit
        </Button>
      </Stack>
      <EvidenceIntelligenceDialog
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        title={`Curriculum audit — ${title || 'Untitled'}`}
        state={auditState}
        result={auditResult}
        error={auditError}
        onRerun={runAudit}
        sections={['recommendations', 'gaps', 'weekly_summary']}
        loadingLabel="Auditing curriculum..."
      />
    </Paper>
  );
};

const DefaultGithubTokenPanel = () => {
  const [hasToken, setHasToken] = useState(false);
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchDefaultGithubTokenStatus()
      .then((result) => {
        if (!cancelled) setHasToken(Boolean(result?.has_token));
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error.message || 'Could not load token status.');
      });
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    if (!token.trim()) return;
    setSaving(true);
    setStatusText('');
    try {
      const result = await patchDefaultGithubToken(token.trim());
      setHasToken(Boolean(result?.has_token));
      setToken('');
      setStatusText('Saved.');
    } catch (error) {
      setStatusText(`Error: ${error.message || 'Could not save token.'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <GitHubIcon fontSize="small" />
        <Typography variant="h6" sx={{ fontWeight: 800 }}>Default GitHub token</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Used to pull real PR/commit facts into hackathon feedback when a cohort week's own
        Setup config token (per-repo, set on that week's card) is left blank. This is a
        platform-wide fallback, not tied to any one cohort's repo.
      </Typography>
      {loadError && <Alert severity="error" sx={{ mb: 1.5 }}>{loadError}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          type="password"
          label="GitHub token"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          size="small"
          fullWidth
          placeholder={hasToken ? 'Token set — leave blank to keep it' : 'e.g. github_pat_...'}
          sx={{ maxWidth: 420 }}
        />
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!token.trim() || saving}
        >
          Save
        </Button>
      </Stack>
      {statusText && (
        <Typography variant="body2" color={statusText.startsWith('Error') ? 'error' : 'text.secondary'} sx={{ mt: 1 }}>
          {statusText}
        </Typography>
      )}
    </Paper>
  );
};

const FeedbackPanel = ({ users }) => {
  const learners = useMemo(() => users.filter((item) => (item.role || 'learner') === 'learner'), [users]);
  const cohortKeys = useMemo(() => [...new Set(learners.map((item) => item.cohort_key).filter(Boolean))], [learners]);
  const [feedbackType, setFeedbackType] = useState('individual');
  const [activityScope, setActivityScope] = useState('general');
  const [cohortKey, setCohortKey] = useState(cohortKeys[0] || '');
  const [userId, setUserId] = useState(learners[0]?.id || '');
  const [focus, setFocus] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [statusText, setStatusText] = useState('');
  const [previewRows, setPreviewRows] = useState([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [messageId, setMessageId] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState('');

  useEffect(() => {
    if (!cohortKey && cohortKeys[0]) setCohortKey(cohortKeys[0]);
    if (!userId && learners[0]?.id) setUserId(learners[0].id);
  }, [cohortKey, cohortKeys, learners, userId]);

  // Cohort feedback broadcasts the same message to every learner in the cohort at
  // once, so it only makes sense for "general" (aggregate, no personal deliverable)
  // scope — hackathon/concept clinic/journal/daily tasks/show & tell/mock interview
  // are all per-learner by nature, and broadcasting one learner's role/deliverable to
  // everyone else reading the same email is a privacy/relevance problem, not just a
  // wording one. Force those scopes back to Individual feedback.
  useEffect(() => {
    if (activityScope !== 'general' && feedbackType === 'cohort') {
      setFeedbackType('individual');
    }
  }, [activityScope, feedbackType]);

  const loadHistory = async () => {
    setHistoryError('');
    try {
      const params = {
        feedback_type: feedbackType,
        activity_scope: activityScope,
        cohort_key: feedbackType === 'cohort' ? cohortKey : '',
        user_id: feedbackType !== 'cohort' ? userId : '',
      };
      const items = await fetchFeedbackMessages(params);
      setHistory(items);
    } catch (error) {
      setHistoryError(error.message || 'Could not load feedback history.');
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackType, activityScope, cohortKey, userId]);

  const generateDraft = async () => {
    setStatusText('Generating feedback draft...');
    try {
      const draft = await draftFeedback({
        feedback_type: feedbackType,
        activity_scope: activityScope,
        cohort_key: cohortKey,
        user_id: userId,
        focus,
      });
      setSubject(draft.subject || '');
      setMessage(draft.message || '');
      setPreviewRows(draft.preview_rows || []);
      setRecipientCount(draft.recipient_count || 0);
      setMessageId(draft.message_id || null);
      setStatusText('Draft ready. Review before sending.');
      loadHistory();
    } catch (error) {
      setStatusText(error.message || 'Could not generate feedback.');
    }
  };

  const deliver = async () => {
    if (!subject.trim() || !message.trim()) {
      setStatusText('Generate or write feedback before sending.');
      return;
    }
    setStatusText('Sending feedback...');
    try {
      const result = await sendFeedback({
        message_id: messageId,
        feedback_type: feedbackType,
        activity_scope: activityScope,
        cohort_key: cohortKey,
        user_id: userId,
        subject,
        message,
        send_email: true,
      });
      setMessageId(result.message_id || messageId);
      setStatusText(`Sent to ${result.recipient_count} recipient${result.recipient_count === 1 ? '' : 's'}.`);
      loadHistory();
    } catch (error) {
      setStatusText(error.message || 'Feedback could not be sent.');
    }
  };

  const loadMessage = (item) => {
    setMessageId(item.id);
    setFeedbackType(item.feedback_type || 'cohort');
    setActivityScope(item.activity_scope || 'general');
    setCohortKey(item.cohort_key || '');
    setUserId(item.user_id || userId);
    setFocus(item.focus || '');
    setSubject(item.subject || '');
    setMessage(item.message || '');
    setPreviewRows(item.signal_snapshot?.preview_rows || []);
    setRecipientCount(item.recipient_count || 0);
    setStatusText(`Loaded ${item.status} feedback #${item.id}.`);
  };

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={900}>Feedback automation</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Draft feedback for one learner, scoped to a specific activity (hackathon, journal, concept clinic, etc.) with Evidence Intelligence, then review and send by email. Cohort-wide broadcasts are only available for Overall learning — every other activity is per-learner, so it's sent as individual feedback instead.
        </Typography>
        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Feedback type"
              value={feedbackType}
              onChange={(event) => setFeedbackType(event.target.value)}
              fullWidth
            >
              <MenuItem value="cohort" disabled={activityScope !== 'general'}>
                Cohort feedback{activityScope !== 'general' ? ' (general scope only)' : ''}
              </MenuItem>
              <MenuItem value="individual">Individual feedback</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Activity scope"
              value={activityScope}
              onChange={(event) => setActivityScope(event.target.value)}
              fullWidth
            >
              <MenuItem value="general">Overall learning</MenuItem>
              <MenuItem value="daily_tasks">Daily tasks</MenuItem>
              <MenuItem value="concept_clinic">Concept clinic</MenuItem>
              <MenuItem value="hackathon">Hackathon</MenuItem>
              <MenuItem value="journal">Journal update</MenuItem>
              <MenuItem value="show_and_tell">Show and tell</MenuItem>
              <MenuItem value="mock_interview">Mock interview</MenuItem>
            </TextField>
          </Grid>
          {feedbackType === 'cohort' ? (
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Cohort"
                value={cohortKey}
                onChange={(event) => setCohortKey(event.target.value)}
                fullWidth
              >
                <MenuItem value="">All learner accounts</MenuItem>
                {cohortKeys.map((key) => <MenuItem key={key} value={key}>{key}</MenuItem>)}
              </TextField>
            </Grid>
          ) : (
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Learner"
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
                fullWidth
              >
                {learners.map((learner) => (
                  <MenuItem key={learner.id} value={learner.id}>
                    {learner.first_name} {learner.last_name} — {learner.email}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} md={3}>
            <TextField
              label="Review focus"
              value={focus}
              onChange={(event) => setFocus(event.target.value)}
              fullWidth
              placeholder="Example: journal quality, PR collaboration, SQL confidence"
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Button variant="contained" onClick={generateDraft}>Generate draft</Button>
              <Button variant="outlined" onClick={deliver} disabled={!message.trim()}>Send email</Button>
              {messageId && <Chip label={`Feedback #${messageId}`} variant="outlined" />}
              {recipientCount > 0 && <Chip label={`${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`} />}
              {statusText && <Typography variant="body2" color="text.secondary">{statusText}</Typography>}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider' }}>
            <Stack spacing={1.5}>
              <TextField
                label="Email subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                fullWidth
              />
              <TextField
                label="Feedback message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                multiline
                minRows={16}
                fullWidth
              />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Stack spacing={2}>
            <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={900}>Draft basis</Typography>
              <Typography variant="caption" color="text.secondary">
                Signals used to produce the current draft.
              </Typography>
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {previewRows.map((row) => (
                  <Box key={row.user.id} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={900}>
                          {row.user.first_name} {row.user.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{row.user.email}</Typography>
                      </Box>
                      <Chip size="small" label={signalLabel[row.status] || row.status} color={signalColor[row.status] || 'default'} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                      {row.recommended_action}
                    </Typography>
                  </Box>
                ))}
                {!previewRows.length && <Alert severity="info">Generate a draft to see the learner signals used.</Alert>}
              </Stack>
            </Paper>
            <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={900}>Feedback history</Typography>
              <Typography variant="caption" color="text.secondary">
                Drafts and sent feedback for this selected scope.
              </Typography>
              {historyError && <Alert severity="warning" sx={{ mt: 1 }}>{historyError}</Alert>}
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {history.map((item) => (
                  <Box key={item.id} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={900} noWrap>{item.subject}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{item.id} · {item.status} · {item.recipient_count} recipient{item.recipient_count === 1 ? '' : 's'}
                        </Typography>
                      </Box>
                      <Button size="small" onClick={() => loadMessage(item)}>Load</Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {item.sent_at ? `Sent ${new Date(item.sent_at).toLocaleString('en-GB')}` : `Created ${item.created_at ? new Date(item.created_at).toLocaleString('en-GB') : 'recently'}`}
                    </Typography>
                  </Box>
                ))}
                {!history.length && <Typography variant="caption" color="text.secondary">No feedback saved for this scope yet.</Typography>}
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

const AdminPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('needs');
  const [tab, setTab] = useState('analytics');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadUsers = (nextQuery = query) => {
    setLoading(true);
    setError('');
    fetchAdminUsers(nextQuery)
      .then((items) => {
        const sorted = [...items].sort((a, b) => {
          const aNeeds = !(a.learning_paths || []).length && !(a.cohort_key || '').trim();
          const bNeeds = !(b.learning_paths || []).length && !(b.cohort_key || '').trim();
          return Number(bNeeds) - Number(aNeeds);
        });
        setUsers(sorted);
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (canAdmin(user)) {
      loadUsers('');
      fetchCohorts().then(setCohorts).catch(() => { });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  if (!canAdmin(user)) {
    return <Navigate to="/my-learning" replace />;
  }

  const updateLocalUser = (updated) => {
    setUsers((current) => current.map((item) => (item.id === updated.id ? updated : item)));
  };

  const removeLocalUser = (userId) => {
    setUsers((current) => current.filter((item) => item.id !== userId));
  };

  const counts = {
    total: users.length,
    needs: users.filter(isNeedsOnboarding).length,
    requests: users.filter((item) => item.onboarding_intent?.submitted_at).length,
    unverified: users.filter((item) => !item.email_verified).length,
  };
  const visibleUsers = users.filter((item) => {
    if (filter === 'needs') return isNeedsOnboarding(item);
    if (filter === 'requests') return Boolean(item.onboarding_intent?.submitted_at);
    if (filter === 'unverified') return !item.email_verified;
    return true;
  });

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
            <AdminPanelSettingsIcon color="primary" />
            <Typography variant="h4" fontWeight={800}>Admin</Typography>
          </Stack>
          <Typography color="text.secondary">
            Manage learner setup, intelligence signals, and platform support tools.
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="analytics" label="Analytics" />
            <Tab value="cohorts" label="Cohorts" />
            <Tab value="users" label="Users" />
            <Tab value="feedback" label="Feedback" />
            <Tab value="tools" label="Tools" />
            <Tab value="settings" label="Settings" />
          </Tabs>
        </Paper>

        {tab === 'analytics' && <LearnerSignalsPanel />}

        {tab === 'cohorts' && <CohortsPanel cohorts={cohorts} onCohortsLoaded={setCohorts} />}

        {tab === 'users' && (
          <Stack spacing={2}>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={1.5}>
              <AdminStat label="Total users" value={counts.total} />
              <AdminStat label="Need setup" value={counts.needs} tone="warning" />
              <AdminStat label="Requests" value={counts.requests} tone={counts.requests ? 'warning' : 'default'} />
              <AdminStat label="Unverified" value={counts.unverified} />
            </Box>

            <InviteLearnerPanel />

            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField
                  label="Search users"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  fullWidth
                  placeholder="Name, email, or cohort"
                />
                <TextField
                  select
                  label="View"
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  sx={{ minWidth: { xs: '100%', md: 190 } }}
                >
                  <MenuItem value="needs">Need setup</MenuItem>
                  <MenuItem value="requests">Onboarding requests</MenuItem>
                  <MenuItem value="unverified">Unverified</MenuItem>
                  <MenuItem value="all">All users</MenuItem>
                </TextField>
                <Button variant="contained" startIcon={<SearchIcon />} onClick={() => loadUsers(query)}>
                  Search
                </Button>
              </Stack>
            </Paper>

            {error && <Alert severity="error">{error}</Alert>}
            {loading ? (
              <Alert severity="info">Loading users...</Alert>
            ) : (
              <Stack spacing={2}>
                {visibleUsers.map((item) => (
                  <AdminUserCard
                    key={item.id}
                    user={item}
                    currentUser={user}
                    cohorts={cohorts}
                    onSaved={updateLocalUser}
                    onDeleted={removeLocalUser}
                  />
                ))}
                {!visibleUsers.length && <Alert severity="info">No users found for this view.</Alert>}
              </Stack>
            )}
          </Stack>
        )}

        {tab === 'feedback' && <FeedbackPanel users={users} />}

        {tab === 'tools' && (
          <Stack spacing={2}>
            <CurriculumAuditPanel />
          </Stack>
        )}

        {tab === 'settings' && (
          <Stack spacing={2}>
            <DefaultGithubTokenPanel />
          </Stack>
        )}
      </Stack>
    </Container>
  );
};

export default AdminPage;
